// Walkthrough (P8) progress and the practice-data helpers (T1-02a, row T003). Progress lives in the person pref `tours`;
// the owner switch lives in the workspace setting `toursEnabled`.
import { z } from "zod";
import { ISO } from "./common.ts";

/** One walkthrough: the step the person reached and when they started, finished or dismissed it. */
export const TOUR_ENTRY = z.strictObject({
  step: z.string().max(80),
  startedAt: ISO,
  doneAt: ISO.optional(),
  dismissedAt: ISO.optional(),
});

/** Every walkthrough a person has touched, keyed by tour id, plus `quiet` (no first-visit prompts for this person). */
export const TOUR_PROGRESS = z.strictObject({
  tours: z.record(z.string().min(1).max(80), TOUR_ENTRY).refine((r) => Object.keys(r).length <= 800, "At most 800 tours."),
  quiet: z.boolean().optional(),
});
export type TourProgress = z.infer<typeof TOUR_PROGRESS>;

/** The owner switch for the first-visit tour prompts. */
export const TOURS_ENABLED = z.boolean();

/** RFC 2606 reserved domain: it can never receive mail, so practice records never reach a real inbox. */
export const PRACTICE_DOMAIN = "practice.invalid";
/** The name prefix every practice record carries, so the agency can tell them apart at a glance. */
export const PRACTICE_PREFIX = "Practice ";

/** True when the part after the last "@" is the practice domain (any case; spaces and "<>" ignored). */
export const isPracticeAddress = (address: string): boolean => {
  const clean = address.replace(/[<>\s]/g, "").toLowerCase();
  const at = clean.lastIndexOf("@");
  return at >= 0 && clean.slice(at + 1) === PRACTICE_DOMAIN;
};

/** The rows that are not practice data (a row is practice when its `practice` flag is true). */
export const notPractice = <T extends { practice?: boolean | null }>(rows: T[]): T[] => rows.filter((r) => r.practice !== true);
